import React, { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import U2000DetailTable from "../../../../../u2000Core/components/tables/detail/U2000DetailTable";
import U2000ActionBtn from "../../../../../u2000Core/components/buttons/U2000ActionBtn";
import { FaPlus, FaRegFile, FaTrashAlt } from "react-icons/fa";
import U2000ResponsiveToolbar from "../../../../../u2000Core/components/toolbar/U2000ResponsiveToolbar";
import { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { u2000IntFormState, useU2000FormStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import u2000FormState from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { U2000FormStateEnumState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { U2000IdColumnDef, type U2000TableColumn } from "../../../../../u2000Core/components/tables/basic/U2000BasicTableList";
import { useU2000AuthContext } from "../../../../../u2000Core/context/auth/U2000UseAuthContext";
import { generateTemporaryId } from "../../../../../u2000Core/tools/U2000IdUtils";
import {
  emptyCreditApplication,
  u3125CanAddCreditApplication,
  u3125CanModifyInvoice,
  u3125FormatCreditApplicationNature,
  u3125FormatInvoiceNumberDisplay,
  u3125IsLegacyCreditApplication,
  type U3125InvoiceCreditApplicationGridRow,
  type U3125InvoiceFormRow,
} from "../../../../U3125Service";
import { NS, U3125ROLES, u3125FormatCurrency } from "../../../../U3125";
import type { U3125FactureNestedParentHandlers } from "../u3125FactureInvoiceLinesTab/U3125InvoiceLineHandlers";
import { useU3125CreditApplication } from "../U3125UseCreditApplication";
import { useU3125CreditApplicationHandler } from "./U3125CreditApplicationHandlers";
import U3125CreditApplicationEditDlg from "./U3125CreditApplicationEditDlg";
import U3125CreditApplicationDeleteDlg from "./U3125CreditApplicationDeleteDlg";

function getColumnsDef(t: TFunction): U2000TableColumn[] {
  return [
    U2000IdColumnDef,
    {
      name: "sourceInvoiceNumber",
      label: t("colCreditSourceNumber", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      isDefaultSortedColumn: true,
      widthClass: "w-[15%]",
      type: "string",
      align: "left",
      display: (_c, value) => u3125FormatInvoiceNumberDisplay(value as string | number | null | undefined),
    },
    {
      name: "sourceInvoiceDate",
      label: t("colCreditSourceDate", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[22%]",
      type: "string",
      align: "left",
      display: (_c, value) => {
        if (value == null || value === "") return "";
        const s = String(value);
        return s.length >= 10 ? s.slice(0, 10) : s;
      },
    },
    {
      name: "sourceNature",
      label: t("colCreditSourceNature", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[30%]",
      type: "string",
      align: "left",
      display: (_c, value) => u3125FormatCreditApplicationNature(value == null ? undefined : String(value), t, NS),
    },
    {
      name: "amountTaxable",
      label: t("colCreditAppliedAmountTaxable", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[14%]",
      type: "number",
      align: "right",
      display: (_c, _v, row) => {
        const app = row as U3125InvoiceCreditApplicationGridRow;
        if (app.sourceNature === "ADVANCE") return "—";
        if (u3125IsLegacyCreditApplication(app)) return "—";
        return u3125FormatCurrency(Number(app.amountTaxable) || 0);
      },
    },
    {
      name: "amountNonTaxable",
      label: t("colCreditAppliedAmountNonTaxable", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[14%]",
      type: "number",
      align: "right",
      display: (_c, _v, row) => {
        const app = row as U3125InvoiceCreditApplicationGridRow;
        if (app.sourceNature === "ADVANCE") return "—";
        if (u3125IsLegacyCreditApplication(app)) return "—";
        return u3125FormatCurrency(Number(app.amountNonTaxable) || 0);
      },
    },
    {
      name: "amount",
      label: t("colCreditAppliedAmount", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[14%]",
      type: "number",
      align: "right",
      display: (_c, v) => u3125FormatCurrency(v),
    },
  ];
}

interface U3125CreditApplicationsTabBodyProps {
  parentFormHandlers: U3125FactureNestedParentHandlers & { getData: () => U3125InvoiceFormRow | null };
}

const U3125CreditApplicationsTabBody: React.FC<U3125CreditApplicationsTabBodyProps> = ({ parentFormHandlers }) => {
  const { t } = useTranslation();
  const { hasRole } = useU2000AuthContext();
  const gridStateHandlers = useU2000GridStateHandlers<U3125InvoiceCreditApplicationGridRow>();

  const invoiceData = parentFormHandlers.getData?.() ?? null;
  const creditApplications = invoiceData?.creditApplications;
  const creditApplicationsKey = (creditApplications ?? [])
    .map((app) => `${app.id ?? ""}|${app.sourceInvoiceId}|${app.amount}|${app.position}`)
    .join(";");
  const readonly = parentFormHandlers.getFormState?.() === U2000FormStateEnumState.READ;

  useU3125CreditApplication(parentFormHandlers, readonly);

  const parentFormHandlersRef = useRef(parentFormHandlers);
  parentFormHandlersRef.current = parentFormHandlers;

  const [formState, formStateDispatch] = React.useReducer(u2000FormState, u2000IntFormState);
  const lineFormStateHandlers = useU2000FormStateHandlers<U3125InvoiceCreditApplicationGridRow>({
    formState,
    formStateDispatch,
    businessHandlers: {
      newRecord: () => emptyCreditApplication(0),
      validateForm: () => ({}),
    },
  });

  const handler = useU3125CreditApplicationHandler({
    gridStateHandlers,
    t,
    ns: NS,
    formStateHandlers: lineFormStateHandlers,
    parentFormHandlers,
  });

  useEffect(() => {
    const columns = getColumnsDef(t);
    gridStateHandlers.setColumns(columns);
    gridStateHandlers.setSelectedFields(columns.filter((col) => col.isDefaultColumn).map((col) => col.name));
    gridStateHandlers.setSelectedSortedFields(columns.filter((col) => col.isDefaultSortedColumn).map((col) => ({ name: col.name, sortOrder: "ASC" as const })));
    const raw = Array.isArray(creditApplications) ? creditApplications : [];
    const existingRows = gridStateHandlers.getData();
    const list = raw.map((app) => {
      const persistedId = app.id?.trim();
      const existingId = persistedId
        ? persistedId
        : existingRows.find((row) => row.sourceInvoiceId === app.sourceInvoiceId && row.position === app.position)?.id;
      return { ...app, id: existingId?.trim() || generateTemporaryId() } as U3125InvoiceCreditApplicationGridRow;
    });
    gridStateHandlers.setData(list);
    gridStateHandlers.setTotalRows(list.length);
    gridStateHandlers.setErrors([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- gridStateHandlers volontairement exclu
  }, [t, creditApplicationsKey]);

  useEffect(() => {
    if (handler.dataChangedEvent === 0) return;
    const inv = parentFormHandlersRef.current.getData();
    if (!inv) return;
    const nextApplications = gridStateHandlers.getData().map((row, idx) => ({ ...row, position: idx }));
    parentFormHandlersRef.current.setData({
      ...inv,
      creditApplications: nextApplications,
      billingTotals: null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- gridStateHandlers volontairement exclu
  }, [handler.dataChangedEvent]);

  const isEditMode =
    parentFormHandlers.getFormState?.() === U2000FormStateEnumState.EDIT && u3125CanModifyInvoice(parentFormHandlers.getData()?.status);
  const canAdd = u3125CanAddCreditApplication(invoiceData?.availableCreditSources ?? [], invoiceData?.creditApplications ?? []);
  const disabledModify = !isEditMode || handler.gridStateHandlers.getSelectedRows().length !== 1;
  const disabledDelete = !isEditMode || handler.gridStateHandlers.getSelectedRows().length === 0;

  const actions = useMemo(
    () => [
      {
        key: "add",
        label: t("btnAdd", { ns: NS }),
        handleClick: handler.onAdd,
        hasRole: hasRole(U3125ROLES.EDIT),
        disabled: !isEditMode || !canAdd,
        icon: FaPlus,
        Component: U2000ActionBtn,
      },
      {
        key: "modify",
        label: t("btnModify", { ns: NS }),
        handleClick: handler.onModify,
        hasRole: hasRole(U3125ROLES.EDIT),
        disabled: disabledModify,
        icon: FaRegFile,
        Component: U2000ActionBtn,
      },
      {
        key: "delete",
        label: t("btnDelete", { ns: NS }),
        handleClick: handler.onDelete,
        hasRole: hasRole(U3125ROLES.EDIT),
        disabled: disabledDelete,
        icon: FaTrashAlt,
        Component: U2000ActionBtn,
      },
    ],
    [t, handler, isEditMode, canAdd, disabledModify, disabledDelete, hasRole],
  );

  return (
    <>
      <U3125CreditApplicationEditDlg u3125CreditApplicationHandler={handler} parentFormHandlers={parentFormHandlers} t={t} NS={NS} />
      <U3125CreditApplicationDeleteDlg u3125CreditApplicationHandler={handler} t={t} NS={NS} />

      <div className="mt-1 mb-1 flex items-center gap-x-2 border-b-2 pb-3">
        <U2000ResponsiveToolbar actions={actions} />
      </div>
      <U2000DetailTable
        gridStateHandlers={gridStateHandlers}
        rowClickFn={(row) => handler.onRowClick(row as U3125InvoiceCreditApplicationGridRow)}
        allowRowSelection={isEditMode}
        label={t("lblCreditApplicationsGrid", { count: gridStateHandlers.getTotalRows(), ns: NS })}
      />
    </>
  );
};

export default U3125CreditApplicationsTabBody;
